/**
 * BuddyBubble — companion text shown above the walking detective.
 *
 * Triggers (only these):
 *   - Initial mount: a one-shot greeting message on page load.
 *   - Idle 7-minute cadence: a fresh message surfaces every 7 minutes.
 *   - Job-state events (`sherlock:buddy-context`): running / done / failed
 *     fire an immediate swap with the job's identifier appended — e.g.
 *     "case closed · 8c2a31f".
 *   - Buddy-click event (`sherlock:buddy-clicked`): the user clicked the
 *     walking detective; surfaces a random witty/playful one-liner from the
 *     click pool ("you rang?", "mind the deerstalker", etc).
 *
 * Each show stays visible for VISIBLE_WINDOW_MS (7s), then auto-hides.
 * Hover pauses the auto-hide so the user has time to read.
 *
 * NOT triggered by: arbitrary clicks elsewhere, keypresses, mousemoves, or
 * route changes. The walking animation is the persistent activity signal —
 * the bubble is purely for notifications.
 *
 * Listens to `sherlock:buddy-mute` for mute/unmute (persisted in
 * `localStorage["sherlock.buddy.muted"]`). Override prop pins the message
 * and skips all timing logic.
 */

import { Box } from '@mui/material';
import type { ReactElement } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { useReducedMotion } from '@/hooks/useReducedMotion';
import type { BuddyContext, IntakePhase } from '@/lib/buddyMessages';
import { pickMessage } from '@/lib/buddyMessages';

export interface BuddyBubbleProps {
  /** Optional explicit override — when set, this exact text is shown instead of cycling. */
  override?: string;
}

// How long the bubble stays visible after each swap, in ms.
// (#3: extended from 7s to 12s)
const VISIBLE_WINDOW_MS = 12_000;

/**
 * Module-level last-seen intake context.
 *
 * BuddyBubble unmounts/remounts when DetectiveFooter's isActive changes
 * (idle branch → walking branch swap). If the intake running event fires
 * just before or during that swap, the new BuddyBubble instance needs to
 * pick up the intake context that was already dispatched. We store it here
 * so mounting instances can seed their context immediately.
 */
let _lastIntakeContext: {
  status: 'running' | 'paused' | 'done' | 'failed';
  phase: IntakePhase;
  coverage?: { covered: number; total: number };
} | null = null;

// Background idle cadence — re-show a fresh message every 7 minutes if
// nothing else has triggered a swap.
const IDLE_INTERVAL_MS = 7 * 60 * 1000;


function readMuted(): boolean {
  try {
    return localStorage.getItem('sherlock.buddy.muted') === 'true';
  } catch {
    return false;
  }
}

export function BuddyBubble({ override }: BuddyBubbleProps): ReactElement | null {
  const location = useLocation();
  const reduced = useReducedMotion();

  // ── State ─────────────────────────────────────────────────────────────────
  const [muted, setMuted] = useState<boolean>(readMuted);
  const [jobContext, setJobContext] = useState<{
    status: 'running' | 'done' | 'failed';
    identifier?: string;
  } | null>(null);
  // Seed from module-level cache so instances that mount after the event still
  // pick up the active intake context (DetectiveFooter isActive transition).
  const [intakeContext, setIntakeContext] = useState<{
    status: 'running' | 'paused' | 'done' | 'failed';
    phase: IntakePhase;
    coverage?: { covered: number; total: number };
  } | null>(() => _lastIntakeContext);
  const [lastMessages, setLastMessages] = useState<string[]>([]);

  // The "visible" message and a fade key used to trigger re-transition.
  // When mounting mid-intake (after DetectiveFooter isActive transition), seed
  // from the cached intake context so we show an intake message immediately.
  const [displayMsg, setDisplayMsg] = useState<string>(() => {
    if (_lastIntakeContext && _lastIntakeContext.status !== 'paused') {
      return pickMessage(
        {
          kind: 'intake',
          status: _lastIntakeContext.status,
          phase: _lastIntakeContext.phase,
          coverage: _lastIntakeContext.coverage,
        },
        [],
      );
    }
    const ctx: BuddyContext = { kind: 'route', path: location.pathname };
    return pickMessage(ctx, []);
  });
  const [opacity, setOpacity] = useState(1);

  // 7-second visibility window — bubble shows briefly then auto-hides.
  // True initially so the user sees a greeting on first load.
  const [visible, setVisible] = useState(true);

  // Hover pause for the swap and the auto-hide.
  const [hovered, setHovered] = useState(false);

  // Refs (kept current via the assignments below).
  const jobContextRef = useRef<typeof jobContext>(null);
  const intakeContextRef = useRef<typeof intakeContext>(null);
  const lastMessagesRef = useRef<string[]>([]);
  const locationRef = useRef(location.pathname);
  const hoveredRef = useRef<boolean>(false);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep refs in sync with state
  jobContextRef.current = jobContext;
  intakeContextRef.current = intakeContext;
  lastMessagesRef.current = lastMessages;
  locationRef.current = location.pathname;
  hoveredRef.current = hovered;

  // ── Auto-hide helper ──────────────────────────────────────────────────────
  // The bubble shows for VISIBLE_WINDOW_MS after each swap then hides. The
  // walking animation is the persistent loader signal — the bubble is only
  // for notifications (running started, done, failed, activity, idle cadence).
  // Hover keeps the bubble visible so the user can read.

  const scheduleAutoHide = useCallback(() => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => {
      if (hoveredRef.current) {
        scheduleAutoHide();
        return;
      }
      setVisible(false);
    }, VISIBLE_WINDOW_MS);
  }, []);

  // ── Message swap helper ───────────────────────────────────────────────────
  //
  // Every swap shows the bubble for VISIBLE_WINDOW_MS then auto-hides.

  const swapMessage = useCallback(() => {
    let ctx: BuddyContext;
    if (intakeContextRef.current && intakeContextRef.current.status !== 'paused') {
      ctx = {
        kind: 'intake',
        status: intakeContextRef.current.status,
        phase: intakeContextRef.current.phase,
        coverage: intakeContextRef.current.coverage,
      };
    } else if (jobContextRef.current) {
      ctx = {
        kind: 'job',
        status: jobContextRef.current.status,
        identifier: jobContextRef.current.identifier,
      };
    } else {
      ctx = { kind: 'route', path: locationRef.current };
    }

    const next = pickMessage(ctx, lastMessagesRef.current);

    if (reduced) {
      // Instant swap — no fade.
      setDisplayMsg(next);
    } else {
      // Fade out → swap text → fade in.
      setOpacity(0);
      setTimeout(() => {
        setDisplayMsg(next);
        setOpacity(1);
      }, 200);
    }

    setLastMessages((prev) => {
      const updated = [...prev, next].slice(-3);
      return updated;
    });

    // Show for 7 seconds, then auto-hide.
    setVisible(true);
    scheduleAutoHide();
  }, [reduced, scheduleAutoHide]);

  // ── Initial show — start the 7s visibility timer on mount ───────────────
  useEffect(() => {
    scheduleAutoHide();
    return () => {
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Route change → clear job context only (no auto-swap) ────────────────
  // Navigation no longer flashes a new dialogue. The route is still tracked
  // (via locationRef) so the next activity-triggered or idle-cadence swap
  // picks from the new route's pool.
  //
  // Exception: the first time the user enters /docs in a session, fire one
  // swap so the buddy actually greets them ("so you want to know how I
  // work?"). Subsequent docs navigation stays silent.

  useEffect(() => {
    setJobContext(null);
    setIntakeContext(null);

    if (location.pathname.startsWith('/docs')) {
      try {
        if (sessionStorage.getItem('sherlock.buddy.docs-greeted') !== 'true') {
          sessionStorage.setItem('sherlock.buddy.docs-greeted', 'true');
          swapMessage();
        }
      } catch {
        // sessionStorage unavailable (private mode, etc.) — skip the greeting
        // rather than spam on every doc nav.
      }
    }
  }, [location.pathname, swapMessage]);

  // ── Background idle cadence — re-show every 7 minutes ────────────────────

  useEffect(() => {
    const id = setInterval(() => {
      // Force a swap (and re-show) on the idle cadence regardless of rate limit.
      swapMessage();
    }, IDLE_INTERVAL_MS);
    return () => clearInterval(id);
  }, [swapMessage]);

  // ── Event listeners ───────────────────────────────────────────────────────

  useEffect(() => {
    function onBuddyContext(e: Event): void {
      const detail = (
        e as CustomEvent<
          | {
              kind: 'job';
              status: 'running' | 'done' | 'failed';
              identifier?: string;
            }
          | {
              kind: 'intake';
              status: 'running' | 'paused' | 'done' | 'failed';
              phase: IntakePhase;
              coverage?: { covered: number; total: number };
              sessionId?: string;
            }
        >
      ).detail;

      if (detail.kind === 'intake') {
        if (detail.status === 'paused') {
          // Round ready — user's turn. Clear intake context so the bubble
          // idles naturally (no message push on paused).
          _lastIntakeContext = null;
          intakeContextRef.current = null;
          setIntakeContext(null);
          // Let the bubble auto-hide per its normal VISIBLE_WINDOW_MS cycle.
          return;
        }
        const next = {
          status: detail.status,
          phase: detail.phase,
          coverage: detail.coverage,
        };
        // Update module-level cache so remounting instances can seed from it.
        _lastIntakeContext = next;
        intakeContextRef.current = next;
        setIntakeContext(next);
        // On done/failed, clear cache after a short delay (allow the celebrate
        // message to show, then let the next mount see no active intake).
        if (detail.status === 'done' || detail.status === 'failed') {
          setTimeout(() => { _lastIntakeContext = null; }, VISIBLE_WINDOW_MS);
          jobContextRef.current = null;
          setJobContext(null);
        }
        swapMessage();
        return;
      }

      // kind === 'job' — investigation / analysis
      // Set context state and force-swap to reflect the new job state.
      // The identifier (e.g. investigation id, issue short hash) is appended
      // to the message by pickMessage when present.
      const next = { status: detail.status, identifier: detail.identifier };
      jobContextRef.current = next;
      setJobContext(next);
      swapMessage();
    }

    function onBuddyMute(e: Event): void {
      const detail = (e as CustomEvent<{ muted: boolean }>).detail;
      setMuted(detail.muted);
    }

    function onBuddyClicked(): void {
      // Force-pick from the click pool — a witty one-liner.
      const next = pickMessage({ kind: 'click' }, lastMessagesRef.current);
      if (reduced) {
        setDisplayMsg(next);
      } else {
        setOpacity(0);
        setTimeout(() => {
          setDisplayMsg(next);
          setOpacity(1);
        }, 200);
      }
      setLastMessages((prev) => [...prev, next].slice(-3));
      setVisible(true);
      scheduleAutoHide();
    }

    window.addEventListener('sherlock:buddy-context', onBuddyContext as EventListener);
    window.addEventListener('sherlock:buddy-mute', onBuddyMute as EventListener);
    window.addEventListener('sherlock:buddy-clicked', onBuddyClicked as EventListener);
    return () => {
      window.removeEventListener('sherlock:buddy-context', onBuddyContext as EventListener);
      window.removeEventListener('sherlock:buddy-mute', onBuddyMute as EventListener);
      window.removeEventListener('sherlock:buddy-clicked', onBuddyClicked as EventListener);
    };
  }, [swapMessage, scheduleAutoHide, reduced]);

  // ── User activity is intentionally NOT a trigger ─────────────────────────
  //
  // The bubble only swaps on:
  //   1. Job-state events (running / done / failed) — see the listener above
  //   2. Idle 7-minute cadence — see the interval below
  //   3. Initial mount (greeting on first load)
  //
  // Clicks and keydowns no longer trigger swaps — keeps the buddy quiet
  // during active work.

  // ── Visibility signal for parents ────────────────────────────────────────
  // The parent (DetectiveFooter) draws the bubble chrome (border, background,
  // tail) around our text. When we return null (muted or auto-hide window
  // expired), the chrome would otherwise render around nothing — a visible
  // empty bubble. Emit a custom event whenever our render decision changes
  // so the parent can gate its chrome to match.

  useEffect(() => {
    const willRender = override !== undefined || (!muted && visible);
    window.dispatchEvent(
      new CustomEvent('sherlock:buddy-bubble-visibility', {
        detail: { visible: willRender },
      }),
    );
  }, [override, muted, visible]);

  // ── Hover reveals the bubble + cancels the auto-hide while hovered ───────
  // (Useful when the user notices the buddy and wants to read the message
  // even after the 7s window has passed.)

  function onHoverEnter(): void {
    setHovered(true);
    setVisible(true);
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
  }
  function onHoverLeave(): void {
    setHovered(false);
    scheduleAutoHide();
  }

  // ── Muted state ───────────────────────────────────────────────────────────

  if (muted && !override) return null;

  // ── Hidden (auto-hide window expired) ─────────────────────────────────────

  if (!override && !visible) return null;

  // ── Override mode ─────────────────────────────────────────────────────────

  if (override) {
    return (
      <Box
        data-testid="buddy-bubble"
        sx={{
          fontFamily: '"JetBrains Mono", monospace',
          fontSize: 13,
          color: 'primary.main',
          whiteSpace: 'normal',
          wordBreak: 'break-word',
        }}
      >
        {override.replace(/^🕵🏽\s*/, '')}
      </Box>
    );
  }

  // ── Normal swap mode ──────────────────────────────────────────────────────

  return (
    <Box
      data-testid="buddy-bubble"
      onMouseEnter={onHoverEnter}
      onMouseLeave={onHoverLeave}
      sx={{
        fontFamily: '"JetBrains Mono", monospace',
        fontSize: 13,
        color: 'primary.main',
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        transition: reduced ? 'none' : 'opacity 200ms ease',
        opacity: reduced ? 1 : opacity,
      }}
    >
      {displayMsg}
    </Box>
  );
}
